import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { MdClose, MdStar, MdStarBorder } from 'react-icons/md';
import StudentLayout from '../components/StudentLayout';
import Loader from '../components/Loader';
import AuthService from '../services/authService';
import axiosInstance from '../utils/axiosInstance';
import theme from '../styles/theme';

const MIN_REASON_LENGTH = 20;

const Wrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 22px;
  color: #111827;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const Subtitle = styled.p`
  margin: 4px 0 0;
  color: #6b7280;
  font-size: 14px;
`;

const CourseBanner = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px 10px;
  border: 1px solid #e0e7ff;
  background: #eef2ff;
  border-radius: 12px;
  padding: 12px 16px;
`;

const CourseBannerCourse = styled.span`
  font-weight: 700;
  font-size: 14px;
  color: #3730a3;
`;

const CourseBannerMeta = styled.span`
  font-size: 12.5px;
  color: #4338ca;
`;

const Section = styled.section`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 14px;
    border-radius: 14px;
  }
`;

const SectionHeading = styled.div`
  margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 17px;
  color: #111827;
`;

const SectionSubtitle = styled.p`
  margin: 4px 0 0;
  font-size: 13px;
  color: #6b7280;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
`;

const Table = styled.table`
  width: 100%;
  min-width: 560px;
  border-collapse: collapse;
  font-size: 13px;

  thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    text-align: left;
    background: #f8fafc;
    color: #64748b;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 10px 14px;
    border-bottom: 1px solid #e5e7eb;
    white-space: nowrap;
  }

  tbody td {
    padding: 12px 14px;
    border-bottom: 1px solid #f1f5f9;
    color: #0f172a;
    vertical-align: middle;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:hover {
    background: #f8fafc;
  }
`;

const SubjectName = styled.div`
  font-weight: 600;
  font-size: 13.5px;
  color: #0f172a;
`;

const SubjectMeta = styled.div`
  margin-top: 2px;
  font-size: 11.5px;
  color: #94a3b8;
`;

const StarRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
`;

const StarButton = styled.button`
  border: none;
  background: transparent;
  padding: 2px;
  cursor: pointer;
  color: ${(p) => (p.$filled ? '#f59e0b' : '#cbd5e1')};
  font-size: ${(p) => p.$size || '20px'};
  display: flex;
  transition: color 0.1s ease, transform 0.1s ease;

  &:hover {
    transform: ${(p) => (p.$interactive ? 'scale(1.15)' : 'none')};
  }
`;

const RateCell = styled.td`
  white-space: nowrap;
`;

const RateAgainLink = styled.button`
  margin-top: 4px;
  border: none;
  background: transparent;
  color: ${theme.primaryDark};
  font-size: 11.5px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const RatedBadge = styled.span`
  margin-left: 6px;
  font-size: 11px;
  color: #94a3b8;
`;

const EmptyState = styled.div`
  padding: 32px 24px;
  border: 2px dashed #dbeafe;
  border-radius: 16px;
  text-align: center;
  color: #0f172a;
  background: #eff6ff;
`;

const EmptyTitle = styled.div`
  font-weight: 700;
  font-size: 16px;
`;

const EmptyText = styled.p`
  margin: 8px 0 0;
  color: #475569;
  font-size: 14px;
`;

const ErrorState = styled(EmptyState)`
  border-color: #fee2e2;
  background: #fef2f2;
  color: #991b1b;
`;

const HistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const HistoryCard = styled.div`
  border: 1px solid #f1f5f9;
  background: #f8fafc;
  border-radius: 12px;
  padding: 14px;
`;

const HistoryTopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const HistorySubject = styled.div`
  font-size: 13.5px;
  font-weight: 600;
  color: #0f172a;
`;

const HistoryLecturer = styled.div`
  margin-top: 2px;
  font-size: 12px;
  color: #64748b;
`;

const HistoryDate = styled.div`
  font-size: 11px;
  color: #94a3b8;
  white-space: nowrap;
`;

const HistoryReason = styled.p`
  margin: 8px 0 0;
  font-size: 13px;
  color: #334155;
  line-height: 1.5;
  white-space: pre-wrap;
`;

// Bottom-sheet-on-mobile modal, matching the pattern used on Submissions /
// Contact.
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.65);
  z-index: 1200;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px 16px;

  @media (max-width: 768px) {
    align-items: flex-end;
    padding: 0;
  }
`;

const ModalCard = styled.div`
  position: relative;
  width: min(560px, 100%);
  background: #ffffff;
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 30px 80px rgba(15, 23, 42, 0.25);
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  display: flex;
  flex-direction: column;

  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 999px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }

  @media (max-width: 768px) {
    width: 100%;
    max-height: 90vh;
    border-radius: 20px 20px 0 0;
    padding: 18px 16px calc(18px + env(safe-area-inset-bottom));
    animation: slideUp 0.25s ease;
  }

  @keyframes slideUp {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
  }
`;

const ModalGrabber = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: block;
    width: 40px;
    height: 4px;
    border-radius: 999px;
    background: #e2e8f0;
    margin: 0 auto 12px;
  }
`;

const ModalCloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  border-radius: 999px;
  border: none;
  background: #f1f5f9;
  color: #475569;
  display: grid;
  place-items: center;
  font-size: 18px;
  cursor: pointer;
  z-index: 2;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: #e2e8f0;
    color: #0f172a;
  }
`;

const ModalIntro = styled.div`
  text-align: center;
  padding: 4px 28px 18px;
`;

const ModalIconBadge = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 16px;
  background: ${theme.gradient};
  color: #ffffff;
  font-size: 24px;
  display: grid;
  place-items: center;
  margin: 0 auto 12px;
  box-shadow: 0 10px 24px rgba(67, 56, 202, 0.28);
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 19px;
  color: #0f172a;
`;

const ModalSubtitle = styled.p`
  margin: 6px 0 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.5;
`;

const FormDivider = styled.div`
  height: 1px;
  background: #f1f5f9;
  margin-bottom: 18px;
`;

const ModalError = styled.div`
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 12px;
  font-size: 14px;
`;

const StarPickerRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-bottom: 6px;
`;

const StarPickerLabel = styled.div`
  text-align: center;
  font-size: 12.5px;
  font-weight: 600;
  color: ${theme.primaryDark};
  min-height: 18px;
  margin-bottom: 18px;
`;

const Field = styled.div`
  & + & {
    margin-top: 14px;
  }
`;

const LabelRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 6px;
`;

const Label = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #374151;
`;

const CharCount = styled.span`
  font-size: 11px;
  color: ${(p) => (p.$ok ? '#16a34a' : '#94a3b8')};
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 110px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  padding: 10px 12px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    border-color: ${theme.primary};
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.14);
  }
`;

const SubmitButton = styled.button`
  margin-top: 18px;
  width: 100%;
  height: 44px;
  border: none;
  border-radius: 10px;
  background: ${theme.gradient};
  color: #ffffff;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: filter 0.15s ease;

  &:hover:not(:disabled) {
    filter: brightness(1.05);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SuccessBanner = styled.div`
  border: 1px solid #bbf7d0;
  background: #f0fdf4;
  color: #166534;
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 12px;
  font-size: 14px;
  text-align: center;
`;

function Stars({ value, onHover, onSelect, interactive, size }) {
  return (
    <StarRow>
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = n <= value;
        const Icon = filled ? MdStar : MdStarBorder;
        return (
          <StarButton
            key={n}
            type="button"
            $filled={filled}
            $interactive={interactive}
            $size={size}
            disabled={!interactive}
            onMouseEnter={interactive ? () => onHover?.(n) : undefined}
            onMouseLeave={interactive ? () => onHover?.(0) : undefined}
            onClick={interactive ? () => onSelect?.(n) : undefined}
            aria-label={`${n} star${n > 1 ? 's' : ''}`}
          >
            <Icon />
          </StarButton>
        );
      })}
    </StarRow>
  );
}

const RATING_LABELS = {
  1: 'Poor',
  2: 'Fair',
  3: 'Good',
  4: 'Very good',
  5: 'Excellent',
};

export default function FavoriteLecturer() {
  const courseInfo = AuthService.getCourseInfo();
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const [rateTarget, setRateTarget] = useState(null);
  const [selectedStars, setSelectedStars] = useState(0);
  const [hoverStars, setHoverStars] = useState(0);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const loadSubjects = () => {
    setLoading(true);
    setError(null);
    axiosInstance
      .get('/lecturer-ratings/subjects', {
        params: courseInfo?.registrationId ? { registrationId: courseInfo.registrationId } : {},
      })
      .then((resp) => setSubjects(Array.isArray(resp?.data?.subjects) ? resp.data.subjects : []))
      .catch((err) => {
        console.error('Failed to load lecturer-rating subjects', err);
        const message = err?.response?.data?.message || "We couldn't load your subjects right now.";
        setError(message);
      })
      .finally(() => setLoading(false));
  };

  const loadHistory = () => {
    setHistoryLoading(true);
    axiosInstance
      .get('/lecturer-ratings/mine')
      .then((resp) => setHistory(Array.isArray(resp?.data) ? resp.data : []))
      .catch((err) => {
        console.error('Failed to load lecturer ratings history', err);
      })
      .finally(() => setHistoryLoading(false));
  };

  useEffect(() => {
    loadSubjects();
    loadHistory();
  }, []);

  const openRate = (subject) => {
    setRateTarget(subject);
    setSelectedStars(0);
    setHoverStars(0);
    setReason('');
    setSubmitError(null);
    setSubmitSuccess(false);
  };

  const closeRate = () => {
    if (submitting) return;
    setRateTarget(null);
  };

  const submitRating = (e) => {
    e.preventDefault();
    if (!rateTarget) return;
    if (selectedStars < 1) {
      setSubmitError('Please select a star rating.');
      return;
    }
    if (reason.trim().length < MIN_REASON_LENGTH) {
      setSubmitError(`Please explain your rating in at least ${MIN_REASON_LENGTH} characters.`);
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    axiosInstance
      .post('/lecturer-ratings', {
        batchSubjectId: rateTarget.batchSubjectId,
        rating: selectedStars,
        reason: reason.trim(),
        registrationId: courseInfo?.registrationId || undefined,
      })
      .then(() => {
        setSubmitSuccess(true);
        loadSubjects();
        loadHistory();
        setTimeout(() => setRateTarget(null), 900);
      })
      .catch((err) => {
        console.error('Failed to submit lecturer rating', err);
        const message = err?.response?.data?.message || 'Unable to submit your rating right now.';
        setSubmitError(message);
      })
      .finally(() => setSubmitting(false));
  };

  const displayedStars = hoverStars || selectedStars;
  const reasonLength = reason.trim().length;

  return (
    <StudentLayout title="My Favorite Lecturer">
      <Wrapper>
        <div>
          <Title>My Favorite Lecturer</Title>
          <Subtitle>Rate your lecturers and tell us why -- your feedback helps improve teaching quality.</Subtitle>
        </div>

        {courseInfo && (
          <CourseBanner>
            <CourseBannerCourse>
              {courseInfo.courseName || courseInfo.courseShortName || 'Selected course'}
            </CourseBannerCourse>
            {courseInfo.batchName && <CourseBannerMeta>Batch: {courseInfo.batchName}</CourseBannerMeta>}
            {courseInfo.registrationNumber && (
              <CourseBannerMeta>Reg No: {courseInfo.registrationNumber}</CourseBannerMeta>
            )}
          </CourseBanner>
        )}

        <Section>
          <SectionHeading>
            <SectionTitle>Your subjects this batch</SectionTitle>
            <SectionSubtitle>Tap the stars to rate the lecturer teaching a subject.</SectionSubtitle>
          </SectionHeading>

          {loading && <Loader message="Loading your subjects…" />}

          {!loading && error && (
            <ErrorState>
              <EmptyTitle>Unable to load subjects</EmptyTitle>
              <EmptyText>{error}</EmptyText>
            </ErrorState>
          )}

          {!loading && !error && subjects.length === 0 && (
            <EmptyState>
              <EmptyTitle>No subjects available yet</EmptyTitle>
              <EmptyText>Once lecturers are assigned to your batch's subjects, they'll appear here.</EmptyText>
            </EmptyState>
          )}

          {!loading && !error && subjects.length > 0 && (
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <th>Subject</th>
                    <th>Lecturer</th>
                    <th>Your rating</th>
                  </tr>
                </thead>
                <tbody>
                  {subjects.map((subject) => (
                    <tr key={subject.batchSubjectId}>
                      <td>
                        <SubjectName>{subject.subjectName}</SubjectName>
                        <SubjectMeta>
                          {subject.subjectShortName ? `${subject.subjectShortName} · ` : ''}
                          Sem {subject.semester || 'N/A'}
                        </SubjectMeta>
                      </td>
                      <td>{subject.staffName}</td>
                      <RateCell>
                        <Stars value={subject.myLastRating || 0} interactive={false} size="17px" />
                        {subject.myRatingsCount > 0 && (
                          <RatedBadge>
                            Rated {subject.myRatingsCount}x
                          </RatedBadge>
                        )}
                        <br />
                        <RateAgainLink type="button" onClick={() => openRate(subject)}>
                          {subject.myRatingsCount > 0 ? 'Rate again' : 'Rate this lecturer'}
                        </RateAgainLink>
                      </RateCell>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>
          )}
        </Section>

        <Section>
          <SectionHeading>
            <SectionTitle>Your feedback history</SectionTitle>
            <SectionSubtitle>Everything you've submitted so far.</SectionSubtitle>
          </SectionHeading>

          {historyLoading && <Loader message="Loading your feedback…" />}

          {!historyLoading && history.length === 0 && (
            <EmptyState>
              <EmptyTitle>No feedback submitted yet</EmptyTitle>
              <EmptyText>Rate a lecturer above and it will show up here.</EmptyText>
            </EmptyState>
          )}

          {!historyLoading && history.length > 0 && (
            <HistoryList>
              {history.map((entry) => (
                <HistoryCard key={entry.id}>
                  <HistoryTopRow>
                    <div>
                      <HistorySubject>{entry.subjectName}</HistorySubject>
                      <HistoryLecturer>{entry.staffName}</HistoryLecturer>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <Stars value={entry.rating} interactive={false} size="15px" />
                      <HistoryDate>
                        {new Date(entry.createdAt).toLocaleDateString(undefined, {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </HistoryDate>
                    </div>
                  </HistoryTopRow>
                  <HistoryReason>{entry.reason}</HistoryReason>
                </HistoryCard>
              ))}
            </HistoryList>
          )}
        </Section>
      </Wrapper>

      {rateTarget && (
        <ModalOverlay role="presentation" onClick={closeRate}>
          <ModalCard role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <ModalGrabber />
            <ModalCloseButton type="button" onClick={closeRate} aria-label="Close">
              <MdClose />
            </ModalCloseButton>

            <ModalIntro>
              <ModalIconBadge>
                <MdStar />
              </ModalIconBadge>
              <ModalTitle>Rate {rateTarget.staffName}</ModalTitle>
              <ModalSubtitle>{rateTarget.subjectName}</ModalSubtitle>
            </ModalIntro>

            <FormDivider />

            {submitSuccess && <SuccessBanner>Thanks for your feedback!</SuccessBanner>}
            {submitError && <ModalError>{submitError}</ModalError>}

            <form onSubmit={submitRating}>
              <StarPickerRow>
                <Stars
                  value={displayedStars}
                  interactive
                  size="34px"
                  onHover={setHoverStars}
                  onSelect={setSelectedStars}
                />
              </StarPickerRow>
              <StarPickerLabel>{displayedStars ? RATING_LABELS[displayedStars] : ' '}</StarPickerLabel>

              <Field>
                <LabelRow>
                  <Label>Why did you rate it this way?</Label>
                  <CharCount $ok={reasonLength >= MIN_REASON_LENGTH}>
                    {reasonLength}/{MIN_REASON_LENGTH} min
                  </CharCount>
                </LabelRow>
                <Textarea
                  placeholder="Share what you liked or what could be better..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  maxLength={1000}
                  autoFocus
                />
              </Field>

              <SubmitButton type="submit" disabled={submitting || selectedStars < 1 || reasonLength < MIN_REASON_LENGTH}>
                {submitting ? 'Submitting…' : 'Submit Rating'}
              </SubmitButton>
            </form>
          </ModalCard>
        </ModalOverlay>
      )}
    </StudentLayout>
  );
}
